import { __assign, __extends, __awaiter, __generator } from 'tslib';
import { liveAnnounce } from 'uitk-react-utils';
import { useLocalization } from '@shared-ui/localization-context';
import { createElement, createRef, Component } from 'react';
import 'uitk-react-input';
import { e as UitkTypeaheadToolbar, U as UitkTypeaheadInput, T as TypeaheadToolbarVersion, b as UitkTypeaheadResults } from './uitk-typeahead-toolbar-2e6e13c8.js';
import 'uitk-react-empty-state';
import 'uitk-react-icons';
import 'uitk-react-text';
import 'uitk-react-skeleton';
import 'uitk-react-button';
import 'uitk-react-toolbar';
import { UitkMenu, UitkMenuTrigger, UitkMenuContainer, PositionType } from 'uitk-react-menu';

var TypeaheadNavKeys;
(function (TypeaheadNavKeys) {
    TypeaheadNavKeys["UP"] = "UP";
    TypeaheadNavKeys["DOWN"] = "DOWN";
})(TypeaheadNavKeys || (TypeaheadNavKeys = {}));
var UitkTypeaheadMenu = function (props) {
    var l10n = useLocalization();
    return createElement(TypeaheadMenu, __assign({ l10n: l10n }, props));
};
var TypeaheadMenu = /** @class */ (function (_super) {
    __extends(TypeaheadMenu, _super);
    function TypeaheadMenu(props) {
        var _this = _super.call(this, props) || this;
        _this.toolbarInputRef = createRef();
        _this.keyPressed = false;
        _this.newAttr = -1;
        _this.state = {
            activeSuggestion: -1,
            categorizedResults: [],
            data: {},
            menu: _this.props.dialog,
            results: [],
            searchTerm: _this.props.term,
            showResults: true
        };
        _this.handleChange = _this.handleChange.bind(_this);
        _this.handleKeyPress = _this.handleKeyPress.bind(_this);
        _this.handleKeyDown = _this.handleKeyDown.bind(_this);
        _this.handleKeyUp = _this.handleKeyUp.bind(_this);
        _this.handleResultItemSelect = _this.handleResultItemSelect.bind(_this);
        _this.focusInput = _this.focusInput.bind(_this);
        _this.announceResults = _this.announceResults.bind(_this);
        _this.onDismiss = _this.onDismiss.bind(_this);
        _this.updateResults = _this.updateResults.bind(_this);
        _this.menuOpened = _this.menuOpened.bind(_this);
        _this.menuClosed = _this.menuClosed.bind(_this);
        _this.resetActiveSuggestion = _this.resetActiveSuggestion.bind(_this);
        return _this;
    }
    TypeaheadMenu.prototype.resetActiveSuggestion = function () {
        this.setState({
            activeSuggestion: -1
        });
    };
    TypeaheadMenu.prototype.handleChange = function (event) {
        this.setState({ searchTerm: event.target.value, showResults: true }, this.focusInput);
        // to reset activeSuggestion when value is cleared
        if (event.target.value === '') {
            this.resetActiveSuggestion();
        }
        this.props.onInputChange && this.props.onInputChange(event);
    };
    TypeaheadMenu.prototype.handleKeyPress = function (event) {
        var _a = this.state, activeSuggestion = _a.activeSuggestion, results = _a.results, searchTerm = _a.searchTerm;
        var searchLen = results && results.length;
        this.keyPressed = true;
        var searchChars = searchTerm && searchTerm.split('');
        if (searchChars) {
            var outputWithQuotes = "'" + searchChars.join("' , '") + " '";
            if (searchTerm !== '' && searchLen) {
                liveAnnounce(this.props.l10n.formatText("typeahead.announceResults.resultsAvailable", outputWithQuotes, searchLen));
            }
        }
        if (event.key === 'Enter' || event.keyCode === 38) {
            event.preventDefault();
        }
        if (event.key === 'Enter') {
            activeSuggestion !== -1 && results[activeSuggestion]
                ? this.handleResultItemSelect(results[activeSuggestion].value, results[activeSuggestion].data)
                : this.handleResultItemSelect(this.state.searchTerm, undefined);
        }
    };
    TypeaheadMenu.prototype.focusInput = function () {
        var toolbarInputRef = this.toolbarInputRef;
        if (toolbarInputRef &&
            toolbarInputRef.current &&
            typeof toolbarInputRef.current.focus === 'function') {
            toolbarInputRef.current.focus();
        }
    };
    TypeaheadMenu.prototype.selectInput = function () {
        var toolbarInputRef = this.toolbarInputRef;
        if (toolbarInputRef &&
            toolbarInputRef.current &&
            typeof toolbarInputRef.current.select === 'function') {
            toolbarInputRef.current.select();
        }
    };
    TypeaheadMenu.prototype.onDismiss = function () {
        this.keyPressed = false;
        if (this.props.onDismiss) {
            this.props.onDismiss();
        }
    };
    TypeaheadMenu.prototype.handleResultItemSelect = function (term, data) {
        this.props.adapter.logItemSelection(data);
        this.setState({
            activeSuggestion: -1,
            searchTerm: term,
            showResults: false
        });
        this.props.onResultItemSelect(term, data);
        liveAnnounce(this.props.l10n.formatText("typeahead.announceResults.itemSelected", term));
        this.onDismiss();
        this.menuClosed();
    };
    TypeaheadMenu.prototype.announceResults = function (results) {
        var searchTerm = this.state.searchTerm;
        var searchChars = searchTerm && searchTerm.split('');
        if (searchChars) {
            var outputWithQuotes = "'" + searchChars.join("' , '") + " '";
            if (results && this.isTypeAheadFocused()) {
                liveAnnounce(this.props.l10n.formatText("typeahead.announceResults.resultsAvailable", outputWithQuotes, [results.length]));
            }
        }
    };
    TypeaheadMenu.prototype.updateResults = function (term, makeRecentHistoryCall) {
        return __awaiter(this, void 0, void 0, function () {
            var results, categorizedResults, queryResults, recentHistoryResults, _a, _b, allResults;
            var _c;
            return __generator(this, function (_d) {
                switch (_d.label) {
                    case 0:
                        this.lastSearchTerm = term;
                        if (!(term !== '' && !!makeRecentHistoryCall)) return [3 /*break*/, 5];
                        if (!this.props.categorize) return [3 /*break*/, 2];
                        return [4 /*yield*/, Promise.all([
                                this.props.adapter.fetchResultsCategorized(term),
                                this.props.adapter.fetchRecentSearchResultsCategorized()
                            ])];
                    case 1:
                        _a = _d.sent();
                        return [3 /*break*/, 4];
                    case 2: return [4 /*yield*/, Promise.all([this.props.adapter.fetchResults(term), this.props.adapter.fetchRecentSearchResults()])];
                    case 3:
                        _a = _d.sent();
                        _d.label = 4;
                    case 4:
                        _c = _a, queryResults = _c[0], recentHistoryResults = _c[1];
                        if (recentHistoryResults && recentHistoryResults.length > 0) {
                            this.props.categorize ? (categorizedResults = recentHistoryResults) : (results = recentHistoryResults);
                        }
                        else {
                            this.props.categorize ? (categorizedResults = queryResults) : (results = queryResults);
                        }
                        return [3 /*break*/, 10];
                    case 5:
                        if (!this.props.categorize) return [3 /*break*/, 7];
                        return [4 /*yield*/, this.props.adapter.fetchResultsCategorized(term)];
                    case 6:
                        _b = (categorizedResults = _d.sent());
                        return [3 /*break*/, 9];
                    case 7: return [4 /*yield*/, this.props.adapter.fetchResults(term)];
                    case 8:
                        _b = (results = _d.sent());
                        _d.label = 9;
                    case 9:
                        _d.label = 10;
                    case 10:
                        if (this.lastSearchTerm !== term) {
                            return [2 /*return*/];
                        }
                        if (results && results.length) {
                            this.props.adapter.logSuggestionShown(term, results);
                            this.announceResults(results);
                            this.setState({ results: results, categorizedResults: [] });
                        }
                        else if (categorizedResults && categorizedResults.length) {
                            this.props.adapter.logSuggestionShown(term, categorizedResults);
                            this.announceResults(categorizedResults);
                            allResults = categorizedResults.flatMap(function (result) { return result.typeaheadResults; });
                            this.setState({ categorizedResults: categorizedResults, results: allResults });
                        }
                        else {
                            this.setState({ results: [], categorizedResults: [] });
                        }
                        return [2 /*return*/];
                }
            });
        });
    };
    TypeaheadMenu.getDerivedStateFromProps = function (nextProps, prevState) {
        var term = nextProps.term !== prevState.searchTerm &&
            !nextProps.dialog
            ? nextProps.term
            : prevState.searchTerm;
        return {
            searchTerm: term
        };
    };
    TypeaheadMenu.prototype.isTypeAheadFocused = function () {
        return this.toolbarInputRef.current === document.activeElement;
    };
    TypeaheadMenu.prototype.componentDidUpdate = function (prevProps, prevState) {
        var _a = this.state, searchTerm = _a.searchTerm, results = _a.results, showResults = _a.showResults;
        var _b = this.props, label = _b.label, value = _b.value;
        var searchLen = results && results.length;
        var searchChars = searchTerm && searchTerm.split('');
        var isTypeAheadFocused = this.isTypeAheadFocused();
        if (searchChars) {
            var outputWithQuotes = "'" + searchChars.join("' , '") + " '";
            if (prevState.searchTerm !== searchTerm &&
                searchTerm !== '' &&
                this.keyPressed === true && isTypeAheadFocused) {
                liveAnnounce(this.props.l10n.formatText("typeahead.announceResults.resultsAvailable", outputWithQuotes, searchLen));
            }
        }
        if (value === '' && searchTerm === '' && isTypeAheadFocused) {
            liveAnnounce(this.props.l10n.formatText("typeahead.announceResults.trigerLabel", label));
        }
        if (prevState.searchTerm === searchTerm &&
            value !== '' &&
            this.keyPressed === false &&
            isTypeAheadFocused) {
            liveAnnounce(this.props.l10n.formatText("typeahead.announceResults.showingResults", label, searchLen, searchTerm));
        }
        if (prevState.searchTerm !== searchTerm) {
            if (showResults) {
                this.setState({
                    results: TypeaheadMenu.placeholder
                });
                this.updateResults(searchTerm);
            }
        }
        this.focusInput();
    };
    TypeaheadMenu.prototype.handleActiveSuggestion = function (direction) {
        var _a = this.state, activeSuggestion = _a.activeSuggestion, results = _a.results, searchTerm = _a.searchTerm;
        var allowExactMatch = this.props.allowExactMatch;
        // extra list item in case of allowExactMatch true
        var extraListItemForSearch = allowExactMatch && searchTerm ? 1 : 0;
        var resultsLength = results.length + extraListItemForSearch;
        var circularListFactor = 0;
        if (direction === TypeaheadNavKeys.UP) {
            circularListFactor = activeSuggestion !== -1 ? resultsLength - 1 : resultsLength;
        }
        else if (direction === TypeaheadNavKeys.DOWN) {
            circularListFactor = resultsLength + 1;
        }
        var activePosition = (activeSuggestion + circularListFactor) % resultsLength;
        var activePositionTab = (this.newAttr + circularListFactor) % resultsLength;
        this.setState({
            activeSuggestion: this.newAttr > -1 ? activePositionTab : activePosition
        });
        this.newAttr = -1;
    };
    TypeaheadMenu.prototype.handleUpArrowKey = function (e) {
        if (e.keyCode === 38) {
            this.handleActiveSuggestion(TypeaheadNavKeys.UP);
        }
    };
    TypeaheadMenu.prototype.handleDownArrowKey = function (e) {
        if (e.keyCode === 40) {
            this.keyPressed = true;
            this.handleActiveSuggestion(TypeaheadNavKeys.DOWN);
        }
    };
    TypeaheadMenu.prototype.handleTabKey = function (e) {
        if (e.keyCode === 9 || (e.shiftKey && e.keyCode === 9)) {
            var attr = e.target.parentElement.getAttribute('data-index');
            if (attr) {
                this.newAttr = +attr;
            }
            else {
                this.newAttr = -1;
            }
        }
    };
    TypeaheadMenu.prototype.handleEscKey = function (e) {
        if (e.keyCode === 27) {
            this.onDismiss();
        }
    };
    TypeaheadMenu.prototype.handleKeyDown = function (e) {
        this.handleDownArrowKey(e);
        this.handleUpArrowKey(e);
        this.handleEscKey(e);
    };
    TypeaheadMenu.prototype.handleKeyUp = function (e) {
        this.handleTabKey(e);
    };
    TypeaheadMenu.prototype.menuOpened = function () {
        this.selectInput();
        var isMenuClosing = this.props.dialog;
        if (this.props.openTypeahead) {
            this.props.openTypeahead();
        }
        if (!isMenuClosing) {
            var searchTerm = this.state.searchTerm;
            this.updateResults(searchTerm, true);
        }
    };
    TypeaheadMenu.prototype.menuClosed = function () {
        if (this.props.adapter &&
            'function' === typeof this.props.adapter.logTypeaheadDismiss) {
            this.props.adapter.logTypeaheadDismiss();
        }
        // to reset activeSuggestion when menu is closed
        this.resetActiveSuggestion();
    };
    TypeaheadMenu.prototype.render = function () {
        var _a = this.props, allowExactMatch = _a.allowExactMatch, dialog = _a.dialog, stid = _a.stid, label = _a.label, listTitle = _a.listTitle, placeholder = _a.placeholder, toolbarVersion = _a.toolbarVersion, handleClear = _a.handleClear, emptyResultPlaceholder = _a.emptyResultPlaceholder;
        var _b = this.state, activeSuggestion = _b.activeSuggestion, results = _b.results, searchTerm = _b.searchTerm, categorizedResults = _b.categorizedResults;
        var resultsToShow = this.props.categorize ?
            {
                items: categorizedResults,
                type: 'categorized'
            }
            : {
                items: results,
                type: 'uncategorized'
            };
        return (
        // removing the role=button fixes https://jira.expedia.biz/browse/WCAG-9195
        // tslint:disable: react-a11y-event-has-role
        createElement("div", { className: "uitk-menu-typeahead-wrapper", onKeyDown: this.handleKeyDown, onKeyUp: this.handleKeyUp, title: label },
            createElement(UitkMenu, { id: stid && stid + "-menu", isOpen: dialog, onTriggerClick: this.menuOpened, onDismiss: this.menuClosed, className: "uitk-typeahead-menu" },
                createElement(UitkMenuTrigger, null, this.props.children),
                createElement(UitkMenuContainer, { position: PositionType.LEFT },
                    createElement(UitkTypeaheadToolbar, { action: this.onDismiss, className: "uitk-typeahead-toolbar", iconLabel: "Close the dialog", toolbarVersion: toolbarVersion, renderType: 'menu', title: "" },
                        createElement(UitkTypeaheadInput, { autoFocus: true, className: " " + (toolbarVersion === TypeaheadToolbarVersion.V2
                                ? 'uitk-typeahead-input-v2'
                                : '') + " ", "data-stid": stid && stid + "-menu-input", domRef: this.toolbarInputRef, hasClearButton: searchTerm.length > 0, hasFloatedLabel: false, hasVisibleLabel: false, label: label, onChange: this.handleChange, onKeyPress: this.handleKeyPress, placeholder: placeholder, value: searchTerm, handleClear: handleClear, id: stid })),
                    createElement(UitkTypeaheadResults, { allowExactMatch: allowExactMatch, onResultItemSelect: this.handleResultItemSelect, results: resultsToShow, searchTerm: searchTerm, stid: stid, listTitle: listTitle, activeSuggestion: activeSuggestion, emptyResultPlaceholder: emptyResultPlaceholder })))));
    };
    TypeaheadMenu.placeholder = Array(5).fill({
        disabled: true,
        icon: 'brightness_1',
        name: ' '
    });
    return TypeaheadMenu;
}(Component));

export { TypeaheadMenu, UitkTypeaheadMenu };
